import { Permission, RelationDto, UploadedFile } from "@/modules/common/types";
import { Lead } from "@/modules/leads/types";
import type { PropertyForListing } from "@/modules/listing-dashboard/dtos/index-v2";
import type { SalesResponseV2 } from "@/modules/sales/dtos";
import { UUID } from "crypto";
import { LucideProps } from "lucide-react";
import { ChangeEvent, ForwardRefExoticComponent, RefAttributes } from "react";
import { StepState } from "@/modules/common/components/ui-kit/stepper/types";
import { ResolveRatingDto } from "../dtos/index-v2";
import { Property } from "@/modules/property-tracks/types";
import { ActivityItem } from "../dtos/index-v2";
import { FollowUpStatusEnum } from "../enums";
import { UserV2 } from "@/modules/user/dtos/index.v2";
import {
  Journey,
  Status as CommonStatus,
} from "@/modules/common/dtos/index.v2";
import { StatusV2 } from "@/modules/statuses/dtos/index.v2";

export interface CommentData {
  id: number;
  message: string;
  created_at: string;
  updated_at: string;
  user_id: number;
  lead_id: number;
  deletedAt: string | null;
  old_lead_id: number | string;
  is_hidden: boolean;
  notify: boolean;
  commenter_name: string;
  commenter_id: number;
  commenter_email: string;
  commenter_status: string;
  comment_type: string;
}
export interface StatusCommentData {
  id: number;
  comment: string;
  created_at: string;
  updated_at: string;
  user_id: number;
  new_status_id: number;
  lead_id: number;
  old_status_id: number;
  google_oci_conversion_sent: boolean;
  facebook_oci_conversion_sent: boolean;
  form_data: null | any;
  comment_type: string;
  oldStatus: {
    id: number;
    status: string;
    created_at: string;
    updated_at: string;
    color: string;
    alerts_schedule: number;
    default: boolean;
    important: boolean;
    custom_alert: boolean;
    sms_txt: string | null;
    sms_enabled: boolean | null;
    organization_id: number | null;
    status_category_id: number;
    next_status_category_id: number;
    include_date: string | null;
    is_transitional: boolean | null;
    notify_super_visor: boolean | null;
    notify_sales_manager: boolean | null;
    pool: boolean;
    weight: number | null;
    sort: number | null;
    negative_flag: boolean;
    status_forms_id: number | null;
    validity_time: number;
    pool_validity_time: number | null;
    end_user_id: number | null;
    is_deleted: boolean;
    new_color: string;
  };
  newStatus: {
    id: number;
    status: string;
    created_at: string;
    updated_at: string;
    color: string;
    alerts_schedule: number | null;
    default: boolean;
    important: boolean;
    custom_alert: boolean;
    sms_txt: string | null;
    sms_enabled: boolean | null;
    organization_id: number | null;
    status_category_id: number;
    next_status_category_id: number;
    include_date: string | null;
    is_transitional: boolean | null;
    notify_super_visor: boolean | null;
    notify_sales_manager: boolean | null;
    pool: boolean;
    weight: number | null;
    sort: number | null;
    negative_flag: boolean;
    status_forms_id: number | null;
    validity_time: number;
    pool_validity_time: number | null;
    end_user_id: number | null;
    is_deleted: boolean;
    new_color: string;
  };
  oldStatusCategory: string;
  newStatusCategory: string;
  commenter_name: string;
  commenter_id: number;
  commenter_email: string;
  negative_flag?: boolean;
}

export interface AssignmentCommentData {
  id: number;
  user_id: number;
  old_user_id: number;
  new_user_id: number;
  assignment_type: string;
  created_at: string;
  updated_at: string;
  user_name: string;
  user_color: string;
  user_new_color: string | null;
  old_user_name: string;
  old_user_color: string;
  old_user_new_color: string;
  new_user_name: string;
  new_user_color: string;
  new_user_new_color: string | null;
  comment_type: string;
}

export interface CommentProps {
  createdAt: string;
  message: string;
  commenter_name: string | undefined;
  comment_type: string;
  old_status?: string;
  new_status?: string;
  old_status_color?: string;
  new_status_color?: string;
  old_assigned_user_name?: string;
  new_assigned_user_name?: string;
  old_assigned_user_color?: string;
  new_assigned_user_color?: string;
  user_color?: string;
  deletedAt?: string | null;
  id?: number;
  old_lead_id?: number | string;
  lead_id?: number | string;
  current_lead_id?: number | string;
  commenter_status?: string;
  user_id?: number;
  negative_flag?: boolean;
  oldStatusCategory?: string;
  newStatusCategory?: string;
  assignment_type?: string;
  formData?: [];
  source?: CommentsSource;
  assignmentReport?: string;
  assignmentReportUrl?: string;
  assignmentUid?: string;
}

export enum AssignmentTypes {
  POOL = "from pool",
  AUTO_ASSIGNMENT = "Automatically",
  MANUAL = "Manually",
  DELEGATION = "using Delegation",
  THROUGH_BULK_UPLOAD = "through bulk upload",
  SINGLE_LEAD = "single lead",
  MANUAL_MULTI_SELECTION = "with manual Multi-Selection",
  ROTATION = "using rotation",
  AUTO_ASSIGNMENT_BUTTON = "Automatically due to Auto Assignment trigger",
  REASSIGNMENT_TO_MANAGER = "through re-assign to manager button",
}

export enum CommentsType {
  HIDDEN_HISTORY = "hiddenHistory",
  ASSIGNMENT = "assignment",
  COMMENT = "comment",
  PROPERTY_TRACK_COMMENT = "PropertyTrack",
  STATUS = "status",
  ALL = "All",
}

export enum CommentsSource {
  OWNERS = "owners",
}

export interface LeadListing {
  id: number;
  owner_name: string;
  property_type: string;
  bedrooms: number;
  unit_area: number;
  actual_price: number;
  maintenance: number;
  last_installment_date: string;
  asking_price: number;
  unit_number: string;
  unit_address: string;
  floor_number: number;
  bathrooms: number;
  garden_area: number;
  remaining_installments: number;
  downpayment: number;
  finishing: string;
  status: string;
  phase: string;
  compound: string;
  compound_id: number;
  owner_id: number;
  image_urls: string[];
  website_listing_id: number;
  owners_listing_id: number;
  ready_by: string;
  updated_at: string;
  listing_agent_email: string;
}
export interface LeadListingsLabel {
  key: keyof PropertyForListing;
  label: string;
}
export interface LeadHistoryItem {
  id: string;
  whodunnit: string;
  updated_at: [string, string];
  [key: string]: [string | number | null, string | number | null] | string;
}

export interface LeadSidebarTabs {
  label: string;
  permissions: Permission[];
}

export type LeadSideBarTab =
  | "Location"
  | "Campaign"
  | "Listings"
  | "Sales"
  | "Collection";

export type EditCampaignLocationProps = {
  campaignLocation: {
    editCampaignLocationModal: boolean;
    setEditCampaignLocationModal: React.Dispatch<React.SetStateAction<boolean>>;
    handleChangeCampaign: (e: ChangeEvent<HTMLInputElement>) => void;
    state: string;
    value: string | null;
  };
  isError: boolean;
};
export interface CommissionStatusUpdates {
  id: number;
  old_status_id: string;
  new_status_id: string;
  form_data: string;
  created_at: string;
  updated_at: string;
  user_id: number;
  sale_id: number;
}
export interface Status {
  status: string;
  id: number;
}
export interface StatusUpdateProps {
  creationDate?: string;
  oldStatus: Status;
  newStatus: Status;
  type: string;
}
export interface Unit {
  unitNumber: number;
  collectionCreationDates: string[];
  oldCollectionStatuses: Status[];
  newCollectionStatuses: Status[];
  paymentCreationDates: string[];
  oldPaymentStatuses: Status[];
  newPaymentStatuses: Status[];
  createdAt?: string;
}
export interface CollectionInfoProps {
  statusUpdates: Unit[];
}
export interface LeadInformationItem {
  [key: string]: string | number | boolean;
}

export type Area = {
  ar: string;
  en: string;
};

export type Developer = {
  ar: string;
  en: string;
};

export type UnitType = {
  type: string;
  minimum_unit_area: number;
  maximum_unit_area: number;
  minimum_price: string;
  minimum_currency: string;
  maximum_price: string;
  maximum_currency: string;
  minimum_bedrooms: number;
  maximum_bedrooms: number;
  minimum_bathrooms: number;
  maximum_bathrooms: number;
};

export type PaymentPlanValue = {
  value: number | null;
  percent: number | null;
};

export type PaymentPlan = {
  id: number;
  name: string | null;
  month_3: PaymentPlanValue;
  month_6: PaymentPlanValue;
  delivery_payment: PaymentPlanValue;
  equal_installments: PaymentPlanValue;
  maintenance: PaymentPlanValue;
  clubhouse: number | null;
  parking: number | null;
  years: number;
  cash_discount: PaymentPlanValue;
  down_payment: PaymentPlanValue;
};

export interface Compound {
  id: number;
  area_id: number;
  developer_id: number;
  name: string;
  slug: string;
  updated_at: string;
  image_path: string;
  nawy_organization_id: number;
  has_offers: boolean;
  area: {
    name: string;
    id: number;
  };
  developer: {
    name: string;
    id: number;
  };
}

export type LeadLocationAction = {
  icon:
    | ForwardRefExoticComponent<
        Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
      >
    | (() => JSX.Element);
  label: "Location" | "Inventory" | "E-Realty";
  customStyles: object;
};

export enum LocationTypes {
  AREA = "Area",
  COMPOUND = "Compound",
  DEVELOPER = "Developer",
  CUSTOM = "Custom",
}

export interface LeadSale {
  property_type: string;
  property_type_id: string;
  unit_value: number;
  compound: {
    name: string;
    id: number;
    area_id: number;
  };
  developer: {
    name: string;
    id: number;
  };
  date_of_reservation: string;
  actual_value: number;
  sales_agent: string;
  sale_category: string;
  unit_number: string;
  unit_location: string;
  date_of_contraction: string;
  linked_sale_type: string;
  sales_type: {
    name: string;
    id: number;
  };
  down_payment: number;
  actual_date_of_contraction: string;
  date_of_downpayment: string;
  years_of_payment: number;
  name_on_contract: string;
  cash_price: number;
  comment: string;
  id: number;
  oldId?: number;
  lead_id: number;
}

export interface LeadSaleWithCommissionCollection extends SalesResponseV2 {
  commission_collection_status_updates: CommissionStatusUpdates[];
  commission_payment_status_updates: CommissionStatusUpdates[];
}

export interface LeadSalesLabel {
  key: keyof LeadSale;
  label: string;
  type: "date" | "number" | "string" | "money";
}

export interface StatusObject {
  alerts_schedule: null | string;
  category_name: null | string;
  category_statuses: null | any[];
  color: string;
  created_at: string;
  custom_alert: boolean;
  default: boolean;
  end_user_id: null | number;
  id: number;
  important: boolean;
  include_date: null | boolean;
  is_deleted: boolean;
  is_transitional: null | boolean;
  negative_flag: boolean;
  new_color: string;
  next_status_category: null | string;
  next_status_category_id: null | number;
  next_statuses: null | any[];
  notify_sales_manager: null | boolean;
  notify_super_visor: null | boolean;
  organization_id: null | number;
  pool: boolean;
  pool_validity_time: number;
  sms_enabled: null | boolean;
  sms_txt: null | string;
  sort: null | number;
  status: string;
  status_category: null | string;
  status_category_id: number;
  status_forms_id: null | number;
  status_with_cat_name: string;
  updated_at: string;
  validity_time: number;
  weight: null | number;
}
export interface CommonFormProps {
  isLoadingSubmition: boolean;
  handleClose: () => void;
}

export interface StaticFormProps extends CommonFormProps {
  handleSaleSubmission: (
    formattedData: any,
    formType: "sale" | "form",
    editedSaleData?: {
      saleUId: string;
      leadUId: string;
    },
  ) => void;
  saleType: string | null;
  sale: SalesResponseV2 | null;
  handleFiles: (files: File[]) => void;
  handleDeleteFile: (fileToDelete: UploadedFile) => void;
  files?: File[];
  paymentFiles?: File[];
  paymentPlanFiles?: File[];
  handlePaymentFiles: (files: File[] | File) => void;
  handlePaymentPlanFiles: (files: File[] | File) => void;
  handleDeletePaymentFiles: (fileToDelete?: UploadedFile) => void;
  statusName?: string;
}

export interface FormFactoryProps extends CommonFormProps {
  submitFun: (
    formattedData: any,
    formType: "sale" | "form",
    editedSaleData?: {
      saleUId: string;
      leadUId: string;
    },
  ) => void;
  formFields: any;
  status?: any;
  shwoComment?: boolean;
  selectedStatus?: any;
 
  onSaveAndCreateTracks?: (values: Record<string, any>) => void;
}

export type StaticFormComponent = (props: StaticFormProps) => JSX.Element;
export type FormFactoryComponent = (props: FormFactoryProps) => JSX.Element;

export type FormComponentType =
  | { type: "static"; component: StaticFormComponent }
  | { type: "factory"; component: FormFactoryComponent };

export interface FormRendererProps {
  statusName: string;
  selectedStatus: StatusV2;
  handleSubmit: (
    formattedData: any,
    formType: "sale" | "form",
    editedSaleData?: {
      saleUId: string;
      leadUId: string;
    },
    // createTracksAfter: user picked "Save & Create Tracks" - on a successful
    // submit the host opens the create-track modal.
    options?: { createTracksAfter?: boolean },
  ) => void;
  isLoadingSubmition: boolean;
  handleClose: () => void;
  formFields: any;
  status: any;
  getSaleType: (
    status: string,
  ) => "Blocked" | "Reserved" | "EOI" | "Contracted" | "Promise" | null;
  handleDeleteFile: (fileToDelete: UploadedFile) => void;
  handleFiles: (files: File | File[]) => void;
  files: File[];
  paymentFiles: File[];
  paymentPlanFiles: File[];
  handlePaymentFiles: (files: File | File[]) => void;
  handlePaymentPlanFiles: (files: File | File[]) => void;
  handleDeletePaymentFiles: (fileToDelete?: UploadedFile) => void;
  erealtyIframeOpenCommentModal: boolean;
  setErealtyIframeOpenCommentModal: (value: boolean) => void;
  erealtyIframeComment: string;
  setErealtyIframeComment: (value: string) => void;
  handleErealtyIframeConfirm: (payload?: any) => Promise<void>;
  erealtyIframePayload?: any;
}

export enum SaleType {
  EOI = "EOI",
  Blocked = "Blocked",
  Reserved = "Reserved",
  Contracted = "Contracted",
  PromiseSale = "Promise",
}

export interface User {
  uid: string;
  name?: string;
  full_name?: string;
  email: string;
  color?: string;
}

export interface EditMethodOfContactProps {
  methodOfContact: {
    handleChangeMethodOfContact: (value: UUID) => void;
    data: any;
    value: string | undefined;
    search: string;
    state: string | number | null;
    selectedMethodOfContactPartner?: UUID | null;
    setSelectedMethodOfContactPartner?: (value: UUID) => void;
    initialMethodOfContactId: UUID | undefined;
    setSearch: (value: string) => void;
    isLoading: boolean;
    selectedPartner?: UUID | null;
  };
  lead: { data: { lead: Lead } } | undefined;
  autoFocus?: boolean;
}

export enum CommissionTypes {
  Percentage = "percentage",
  Fixed = "fixed",
}

export enum UnitIdTypes {
  Nawy = "nawy",
  Outside = "outside",
}
export interface LeadReferral {
  uid: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  referrer: {
    uid: string;
    deletedAt: string | null;
    createdAt: string;
    updatedAt: string;
    name: string;
    email: string;
    phoneNumber: string;
    color: string | null;
    status: string;
    profilePictureUrl: string | null;
    banned: boolean;
  };
  leadType: {
    uid: string;
    deletedAt: string | null;
    createdAt: string;
    updatedAt: string;
    name: string;
    default: boolean;
    allowUpdate: boolean;
    allowReferrals: boolean;
    allowUserUpdates: boolean;
  };
  budget: number | null;
  numberOfUnits: number | null;
  validityExpirationDate: string | null;
  isExpired: boolean;
}

export interface LeadReferralResponse {
  data: LeadReferral[];
}

export type FunnelStage = {
  id: number;
  name: string;
  order: number;
  activeIcon?: string;
  inactiveIcon?: string;
  state: StepState;
};

export type FunnelStageResponse = {
  data: FunnelStage[];
};

export type InterestActionType = {
  id: number;
  uid?: string;
  name: string;
  color?: string;
  closingAction?: boolean;
  interestType?: RelationDto;
  leadStatus?: RelationDto;
  trackStatus?: RelationDto;
};

export type InterestAction = {
  id: number;
  uid: string;
  comment?: string;
  interestActionType: InterestActionType;
  addedBy?: User;
  createdAt: string;
};

export type InterestProperty = {
  uid: string;
  interestType: RelationDto;
  lastAction?: InterestAction;
  property: Property;
  track?: { uid: string };
};

export interface InterestPropertyResponse {
  total: number;
  pageSize: number;
  page: number;
  results: InterestProperty[];
}

export interface AddInterestActionInput {
  interestActionTypeId: number;
  interestUid: string;
  comment?: string;
}

export interface QueryInterestActionsCursorInput {
  interestUids: string[];
  take: number;
  cursorId?: number;
  cursorDate?: Date;
}

export interface CursorInterestActionsResponse {
  data: InterestAction[];
  nextCursorId?: number;
  count: number;
}

export interface PropertiesForInterestsResponse {
  data: {
    total: number;
    pageSize: number;
    page: number;
    results: Property[];
  };
}

export interface AddInterestInput {
  leadTypeUid: string;
  journeyUid: string;
  propertyUids: string[];
}

export interface ResolveRatingParams {
  ratingUId: string;
  data: ResolveRatingDto;
}

export interface UseResolveRatingOptions {
  ratingUId: string;
  onSuccess?: () => void;
}

export interface RatingCommentData {
  id?: number;
  uid?: string;
  comment: string;
  createdAt: string | Date;
  user?: UserV2 | null;
}

export interface RatingCustomer {
  id?: number;
  uid?: string;
  name?: string;
  telephones?: {
    default?: boolean;
    telephone?: string;
    unifiedTelephone?: string;
  }[];
}

export interface Rating {
  uid?: string;
  agent?: UserV2 | undefined;
  rating?: number;
  comment?: string;
  comments?: RatingCommentData[];
  status: CommonStatus;
  followUpStatus?: FollowUpStatusEnum;
  createdAt?: string | Date;
  touchpoint?: string;
  journey?: Journey;
}

export interface RatingCardProps {
  id?: string;
  ratingUId: string;
  agent?: UserV2 | null;
  rating?: number;
  comment?: string;
  touchpoint?: string;
  followUpStatus?: FollowUpStatusEnum;
  createdAt?: string | Date;
  comments?: RatingCommentData[];
  findInActivity?: () => void;
  onResolvedFollowUp?: () => void;
  isInThread?: boolean;
  isHighlighted?: boolean;
}

export interface RatingThreadDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  ratingUId: string;
  agent?: UserV2 | null;
  rating?: number;
  comment?: string;
  touchpoint?: string;
  followUpStatus?: FollowUpStatusEnum;
  createdAt?: string | Date;
  comments?: RatingCommentData[];
  findInActivity?: () => void;
  onResolvedFollowUp?: () => void;
  isLoading?: boolean;
}

export interface RatingActivityCardProps {
  ratingUId: string;
  actorName?: string | null;
  actorColor?: string | null;
  agentName?: string | null;
  agentColor?: string | null;
  rating?: number;
  comment?: string;
  date?: string;
  followUpStatus?: string | null;
}

export type JourneyRelationType =
  | "detailed"
  | "full"
  | "basic"
  | "min"
  | "get-lead"
  | "update-journey-relations"
  | "merge-customers"
  | "update-lead-relations"
  | "update-lead"
  | "update-customer";

export enum JourneyRelationModes {
  DETAILED = "detailed",
  FULL = "full",
  MIN = "min",
  BASIC = "basic",
  GET_LEAD = "get-lead",
  MERGE_CUSTOMER = "merge-customers",
  UPDATE_JOURNEY_RELATIONS = "update-journey-relations",
  UPDATE_LEAD_RELATIONS = "update-lead-relations",
  UPDATE_LEAD = "update-lead",
  UPDATE_CUSTOMER = "update-customer",
}

export interface BatchInfo {
  batchLinks: Array<{ title?: string; link?: string }>;
  isLastBatch: boolean;
  remainingCount: number;
}

export interface ConversationSummaryInfoProps {
  item: ActivityItem;
}

export interface ConversationSummaryContent {
  title?: string;
  properties?: Array<{
    title?: string;
    link?: string;
  }>;
  summary?: string;
}

export interface RespondIOSessionSummaryContent {
  summary: string;
  respondAction?: string;
  targetStatusName?: string;
  askedUnitPrice?: number;
  trackUid?: string;
}
export interface PropertyPriceUpdateContent {
  trackUIds: string[];
  summary: string;
}

export interface PropertyPriceUpdateInfoProps {
  item: ActivityItem;
}

// --- Status-update form footer (Save / Save & Create Tracks flow) ---

export interface FormFooterExtraAction {
  text: string;
  onClick: () => void;
  isSubmitting?: boolean;
  testId?: string;
}

// Extra submit intent flags threaded from the form footer buttons.
// createTracksAfter: the user clicked "Save & Create Tracks" instead of the
// plain "Save" - after a successful submit the create-track modal opens.
export type SubmitOptions = { createTracksAfter?: boolean };

export type StatusFormSubmitAction = "save" | "saveAndCreateTracks";
